"use client";

/**
 * Global Error Boundary
 * Catches errors in the root layout (including hydration errors)
 * This is a fallback when even the layout fails to render
 */

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log error to console
    console.error("[Global Error]", {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });

    // Capture error in Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        errorType: "global_error",
        digest: error.digest,
      },
      extra: {
        url: typeof window !== "undefined" ? window.location.href : "unknown",
      },
    });

    // Attempt to send error to server (may fail if app is completely broken)
    fetch("/api/log-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "global_error",
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        url: typeof window !== "undefined" ? window.location.href : "unknown",
      }),
    }).catch(() => {
      // Ignore logging failures
    });
  }, [error]);

  // Must include html and body tags since this replaces the entire page
  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: "#F5F1EB",
          color: "#151324",
        }}
      >
        <div
          style={{
            maxWidth: "400px",
            padding: "32px",
            textAlign: "center",
            backgroundColor: "#E9E5E1",
            borderRadius: "16px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 24px",
              borderRadius: "50%",
              backgroundColor: "rgba(220, 38, 38, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          <h1
            style={{
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            Application Error
          </h1>

          <p
            style={{
              fontSize: "14px",
              color: "#151324",
              opacity: 0.7,
              marginBottom: "24px",
            }}
          >
            A critical error occurred. Please try refreshing the page.
            {error.digest && (
              <span
                style={{
                  display: "block",
                  marginTop: "8px",
                  fontSize: "12px",
                  fontFamily: "monospace",
                  opacity: 0.5,
                }}
              >
                Error ID: {error.digest}
              </span>
            )}
          </p>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={reset}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#F5F1EB",
                backgroundColor: "#59344F",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#151324",
                backgroundColor: "transparent",
                border: "1px solid rgba(21, 19, 36, 0.2)",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
